import { Box } from "@chakra-ui/react";
import {
  ColorIcon,
  customEvent,
  Dropdown,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  Spacing,
} from "goi_common";
import { baseColors, BaseButton, BaseStack } from "goi_common/src/design-system";
import { forwardRef, ReactNode, Reducer, useMemo, useReducer } from "react";
import { 장례준비긴급도_LIST } from "@/containers/MatchContainer/constants/request";
import { Highlight } from "@/components/PageBlocks";
import AllQuotationRequestButtonGroup from "../AllQuotationRequest/AllQuotationRequestButtonGroup";
import { SelectedOption } from "../AllQuotationRequest";
import {
  GUEST_OPTIONS,
  OPS_GUIDE_MESSAGE,
  SANGJO_LANDING_EVENT_PATH,
  SangjoLandingTrigger,
  URGENT_STATUS,
} from "./sangjoLandingData";
import { Accent, LandingSection, SectionTitle } from "./SangjoLandingUI";

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({ key: sido[1] as string, value: sido[0] }));
const STATUS_OPTIONS = 장례준비긴급도_LIST.map((s) => ({ key: s, value: s }));

export interface SangjoLandingFormValue {
  regionText: string;
  condolers: string;
  status: string;
}

/**
 * 상조 랜딩 리드 폼(스크롤 앵커).
 * - 위 버튼: 기존 AllQuotationRequestButtonGroup → 견적 생성 후 /login(카카오 로그인)
 * - 아래 버튼: 상담 바텀시트(전화번호 리드)
 */
const SangjoLandingForm = forwardRef<
  HTMLDivElement,
  {
    submitted: boolean;
    onCall: () => void;
    onOpenConsult: (value: SangjoLandingFormValue) => void;
  }
>(function SangjoLandingForm({ submitted, onCall, onOpenConsult }, ref) {
  const [opt, setOpt] = useReducer<Reducer<SelectedOption, Partial<SelectedOption>>>(
    (prev, next) => ({ ...prev, ...next }),
    { 장례준비긴급도: "", sido: null, sigungu: null, 조문객수: "" },
  );

  const sigunguValues = useMemo(() => {
    if (!opt.sido) return [];
    return Array.from(FUNERAL_SIGUNGU.get(opt.sido) ?? new Map()).map(([code, name]) => ({
      key: name as string,
      value: code as number,
    }));
  }, [opt.sido]);

  const regionText = useMemo(() => {
    const sidoName = SIDO_LIST.find((s) => s.value === opt.sido)?.key ?? "";
    const sigunguName = sigunguValues.find((s) => s.value === opt.sigungu)?.key ?? "";
    return [sidoName, sigunguName].filter(Boolean).join(" ");
  }, [opt.sido, opt.sigungu, sigunguValues]);

  const estimate = GUEST_OPTIONS.find((g) => g.condolers === opt.조문객수)?.estimate;
  const disabled = !opt.sido || !opt.sigungu || !opt.조문객수 || !opt.장례준비긴급도;

  const fireEvent = (trigger_and_target: SangjoLandingTrigger) =>
    customEvent({ newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target } });

  return (
    <Box ref={ref} width="100%">
      <LandingSection>
        <Box className="body_2_normal_sb" color={baseColors.primary_orange}>
          무료 · 1분이면 끝나요
        </Box>
        <Spacing gap={8} />
        <SectionTitle>
          상조부터 <Accent>장례 전체 비용</Accent>까지,
          <br />
          알아보는 것부터 시작하세요
        </SectionTitle>
        <Spacing gap={12} />
        <Box className="body_2_reading_r" color={baseColors.label_alternative_1}>
          <Box
            as="span"
            color={baseColors.label_normal}
            textDecoration="underline"
            style={{ textUnderlineOffset: "3px" }}
          >
            6,500건 이상의 실제 장례 진행 데이터
          </Box>
          를 바탕으로
          <br />
          <Highlight>장례식장별 비용</Highlight>, <Highlight>상조</Highlight>,{" "}
          <Highlight>화장</Highlight>, <Highlight>장지</Highlight>까지 우리 가족에게 필요한 전체
          예상 비용을 안내해 드려요.
        </Box>

        <Spacing gap={32} />
        {submitted ? (
          <BaseStack
            p="32px 20px"
            borderRadius="12px"
            bg={baseColors.background_accent_1}
            alignItems="center"
            textAlign="center"
            spacing={8}
          >
            <Box className="headline_1_sb" color={baseColors.label_normal}>
              상담 신청이 완료되었어요
            </Box>
            <Box className="body_2_reading_r" color={baseColors.label_alternative_1}>
              장례 전문 상담원이 곧 전화를 드려요.
              <br />
              혹시 받지 못하셔도 다시 전화를 드리니
              <br />
              걱정하지 마시고 조금만 기다려 주세요.
            </Box>
          </BaseStack>
        ) : (
          <BaseStack spacing={24}>
            <Field label="장례 예상 지역">
              <BaseStack direction="horizontal" spacing={8}>
                <Box flex={1} minWidth={0}>
                  <Dropdown
                    name="시/도"
                    width="100%"
                    forceZIndex={1}
                    optionsZIndex={999}
                    value={opt.sido ?? undefined}
                    values={SIDO_LIST}
                    onChange={(v) => {
                      setOpt({ sido: (v as number) ?? null, sigungu: null });
                      fireEvent("form_region-answer");
                    }}
                  />
                </Box>
                <Box flex={1} minWidth={0}>
                  <Dropdown
                    name="시/군/구"
                    width="100%"
                    forceZIndex={1}
                    optionsZIndex={999}
                    value={opt.sigungu ?? undefined}
                    values={sigunguValues}
                    onChange={(v) => {
                      setOpt({ sigungu: (v as number) ?? null });
                      fireEvent("form_sigungu-answer");
                    }}
                  />
                </Box>
              </BaseStack>
            </Field>

            <Field label="예상 조문객 수" helper="정확하지 않아도 돼요">
              <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="8px">
                {GUEST_OPTIONS.map((guest) => {
                  const selected = opt.조문객수 === guest.condolers;
                  return (
                    <Box
                      key={guest.condolers}
                      as="button"
                      height="56px"
                      borderRadius="6px"
                      cursor="pointer"
                      bg={selected ? baseColors.label_normal : baseColors.background_normal}
                      boxShadow={selected ? "none" : `inset 0 0 0 1px ${baseColors.line_normal}`}
                      onClick={() => {
                        setOpt({ 조문객수: guest.condolers });
                        fireEvent("form_guest-answer");
                      }}
                    >
                      <Box
                        className="body_2_normal_sb"
                        color={selected ? baseColors.white : baseColors.label_alternative_1}
                      >
                        {guest.label}
                      </Box>
                      <Box
                        className="caption_1_r"
                        color={selected ? baseColors.label_muted_1 : baseColors.label_assistive_1}
                      >
                        {guest.desc}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Spacing gap={8} />
              <Box p="14px 16px" borderRadius="6px" bg={baseColors.background_accent_1}>
                {estimate ? (
                  <>
                    <BaseStack
                      direction="horizontal"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box className="label_1_m" color={baseColors.label_alternative_1}>
                        예상 상조 서비스 비용
                      </Box>
                      <BaseStack direction="horizontal" spacing={6} alignItems="baseline">
                        {estimate.basePrice && (
                          <Box
                            className="caption_1_r"
                            color={baseColors.green_500}
                            textDecoration="line-through"
                          >
                            {estimate.basePrice}
                          </Box>
                        )}
                        <Box className="headline_2_sb" color={baseColors.primary_orange}>
                          {estimate.price}
                        </Box>
                      </BaseStack>
                    </BaseStack>
                    <Box
                      className="caption_1_r"
                      color={
                        estimate.isDeductNote ? baseColors.green_500 : baseColors.label_assistive_1
                      }
                      textAlign="right"
                    >
                      {estimate.note}
                    </Box>
                  </>
                ) : (
                  <Box className="label_2_r" color={baseColors.label_assistive_1}>
                    조문객 수를 고르면 예상 상조 서비스 비용을 바로 보여드려요.
                  </Box>
                )}
              </Box>
            </Field>

            <Field label="현재 상황">
              <Dropdown
                name="선택해 주세요"
                width="100%"
                forceZIndex={1}
                optionsZIndex={999}
                value={opt.장례준비긴급도 || undefined}
                values={STATUS_OPTIONS}
                onChange={(v) => {
                  setOpt({ 장례준비긴급도: (v as string) ?? "" });
                  fireEvent("form_status-answer");
                }}
              />
              {opt.장례준비긴급도 === URGENT_STATUS && (
                <>
                  <Spacing gap={8} />
                  <BaseStack
                    direction="horizontal"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={12}
                    p="16px"
                    borderRadius="6px"
                    bg={baseColors.background_accent_1}
                  >
                    <Box flex={1} minWidth={0}>
                      <Box className="label_1_sb" color={baseColors.label_normal}>
                        지금 급하시다면
                      </Box>
                      <Box className="label_2_r" color={baseColors.label_assistive_1}>
                        24시간 바로 전화 연결돼요
                      </Box>
                    </Box>
                    <Box flexShrink={0}>
                      <BaseButton
                        type="secondary"
                        size="medium"
                        onClick={() => {
                          fireEvent("form_urgent_call");
                          onCall();
                        }}
                      >
                        <BaseStack direction="horizontal" spacing={4} alignItems="center" px="4px">
                          <ColorIcon name="icon-call-mono" size={18} color={baseColors.white} />
                          <Box className="label_1_m">전화하기</Box>
                        </BaseStack>
                      </BaseButton>
                    </Box>
                  </BaseStack>
                </>
              )}
            </Field>

            <BaseStack spacing={8}>
              <AllQuotationRequestButtonGroup
                isTesting
                selectedOption={opt}
                disabled={disabled}
                submitText="내 조건으로 예상 비용 자세히 안내받기"
                loginEventFrom="uv_mtch_rqst"
                memo={OPS_GUIDE_MESSAGE}
                submitBorderRadius="6px"
                submitHeight="48px"
                onSubmitAttempt={() => fireEvent("form_submit")}
                onSubmitSuccess={() => fireEvent("form_submit_success")}
              />
              <BaseButton
                type="assistive_1"
                outlined
                size="large"
                onClick={() => {
                  fireEvent("form_consult");
                  onOpenConsult({
                    regionText,
                    condolers: opt.조문객수,
                    status: opt.장례준비긴급도,
                  });
                }}
              >
                <Box className="body_1_normal_m">정확한 견적 상담 신청하기</Box>
              </BaseButton>
            </BaseStack>
          </BaseStack>
        )}
      </LandingSection>
    </Box>
  );
});

export default SangjoLandingForm;

const Field = ({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: ReactNode;
}) => (
  <Box>
    <BaseStack direction="horizontal" spacing={6} alignItems="baseline" pb="8px">
      <Box className="label_1_sb" color={baseColors.label_normal}>
        {label}
      </Box>
      {helper && (
        <Box className="label_2_r" color={baseColors.label_assistive_1}>
          {helper}
        </Box>
      )}
    </BaseStack>
    {children}
  </Box>
);
